// =====================================================================
// myCRM — Leads (list + detail) and lifecycle dialogs
// Split from the former contacts-leads.jsx so each page lives in a
// single file. Shared MEDDPICCR / ICP helpers stay in pages/contacts.jsx
// (they're contact-centric, not lead-centric).
// =====================================================================

// Fallback DQ reasons used when settings haven't loaded or are missing
// the dqReasons key. The authoritative list comes from D.SETTINGS.dqReasons
// (configurable per org via Admin → Customisation).
const DQ_REASONS_FALLBACK = [
  { label: "Budget",       outcome: "Nurture",      recallMonths: 6 },
  { label: "Authority",    outcome: "Nurture",      recallMonths: 3 },
  { label: "No need",      outcome: "Disqualified", recallMonths: null },
  { label: "Timeline",     outcome: "Nurture",      recallMonths: 6 },
  { label: "Competitor",   outcome: "Disqualified", recallMonths: null },
  { label: "Unresponsive", outcome: "Nurture",      recallMonths: 3 },
  { label: "Other",        outcome: "Disqualified", recallMonths: null },
];

function getDqReasons() {
  const fromSettings = D.SETTINGS && D.SETTINGS.dqReasons;
  return Array.isArray(fromSettings) && fromSettings.length ? fromSettings : DQ_REASONS_FALLBACK;
}

function todayPlusMonths(months) {
  if (months == null) return "";
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

// "May 26"-style month + 2-digit year, used by the MQL / SQL / Intent toggles.
function fmtMonYY(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "";
  return dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

// Builds an absolute LinkedIn profile URL from a stored handle/slug — the
// value is just the part after /in/ (e.g. "jane-doe"). Any prefix that was
// pasted in (full URL, "linkedin.com/in/…") is stripped before re-prefixing
// so the result is always exactly one "https://www.linkedin.com/in/<slug>".
function linkedinHref(v) {
  if (!v) return null;
  const slug = String(v).trim()
    .replace(/^https?:\/\//i, "")
    .replace(/^(www\.)?linkedin\.com\/in\//i, "")
    .replace(/^\/+|\/+$/g, "");
  return slug ? "https://www.linkedin.com/in/" + slug : null;
}

// Option lists reused across the New-lead form and the editable detail.
const SENIORITIES = ["C-Suite", "VP", "Director", "Manager", "Individual Contributor"];
const CONSENT_OPTIONS = ["Unknown", "Opted in", "Opted out", "Legitimate interest"];

// ── Inline editors for the lead detail overview ──────────────────────
// Each control writes immediately through the onSave / onChange it is given
// (which calls tpAPI.leads.update + a local re-render in LeadDetail).
function KvSelect({ value, options, blank = "— None —", onChange }) {
  return (
    <select className="kv-edit" value={value || ""} onChange={e => onChange(e.target.value || null)}>
      {blank !== null && <option value="">{blank}</option>}
      {options.map(o => (typeof o === "string"
        ? <option key={o} value={o}>{o}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>))}
    </select>
  );
}

function KvText({ value, onSave, type = "text", placeholder, allowEmpty = true }) {
  const norm = v => (v == null ? "" : String(v));
  const [v, setV] = useState(norm(value));
  useEffect(() => { setV(norm(value)); }, [value]);
  function commit() {
    const next = v.trim();
    if (next === norm(value)) return;                              // unchanged
    if (next === "" && !allowEmpty) { setV(norm(value)); return; } // required: revert
    onSave(next === "" ? null : next);
  }
  return (
    <input
      className="kv-edit"
      type={type}
      value={v}
      placeholder={placeholder}
      onChange={e => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
    />
  );
}

// On/off toggle backed by a date column: ticking stamps today's date,
// unticking clears it. Renders the stored date as "Mon YY" when on.
function DateToggle({ date, onToggle }) {
  const on = !!date;
  return (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
      <input type="checkbox" checked={on} onChange={e => onToggle(e.target.checked)} />
      {on ? <Badge tone="green">{fmtMonYY(date)}</Badge> : <span className="muted">Off</span>}
    </label>
  );
}

function NewLeadDialog({ onClose, navigate }) {
  const [form, setForm] = useState({
    first: "", last: "", title: "", seniority: "", dept: "",
    email: "", phone: "", linkedin: "",
    company: "", headcount: "", industry: "", region: "",
    source: "", sourceDetail: "", consent: "Unknown",
    owner: "", shortlisted: false, group: "none",
  });
  // Existing group names, offered as autocomplete so the same label is
  // reused rather than re-typed (and mis-spelled) across leads.
  const groupSuggestions = [...new Set((D.LEADS || []).map(l => l.group).filter(g => g && g !== "none"))].sort();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.first.trim() || !form.last.trim()) {
      setError("First and last name are required.");
      return;
    }
    if (!form.company.trim()) {
      setError("Company is required.");
      return;
    }
    const email = (form.email || "").trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError("A valid email is required.");
      return;
    }
    setSaving(true); setError(null);
    try {
      const id = "l_" + Math.random().toString(36).slice(2, 10);
      const res = await tpAPI.leads.create({
        id,
        first: form.first.trim(),
        last: form.last.trim(),
        title: form.title.trim() || null,
        seniority: form.seniority || null,
        dept: form.dept.trim() || null,
        email,
        phone: form.phone.trim() || null,
        linkedin: form.linkedin.trim() || null,
        company: form.company.trim(),
        headcount: form.headcount.trim() || null,
        industry: form.industry.trim() || null,
        region: form.region.trim() || null,
        source: form.source || null,
        sourceDetail: form.sourceDetail.trim() || null,
        consent: form.consent,
        owner: form.owner || null,
        status: "New",
        shortlisted: form.shortlisted,
        group: (form.group || "").trim() || "none",
        created: new Date().toISOString().slice(0, 10),
      });
      onClose();
      navigate("sales/leads/" + (res.id || id));
    } catch (e) {
      setError(e.message || "Failed to create lead.");
      setSaving(false);
    }
  }

  return (
    <Dialog title="New lead" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
        {saving ? "Creating…" : "Create lead"}
      </button>
    </>}>
      <div className="form-row">
        <div className="form-field"><label>First name *</label>
          <input value={form.first} onChange={e => set("first", e.target.value)} autoFocus />
        </div>
        <div className="form-field"><label>Last name *</label>
          <input value={form.last} onChange={e => set("last", e.target.value)} />
        </div>
      </div>
      <div className="form-field"><label>Company *</label>
        <input value={form.company} onChange={e => set("company", e.target.value)} placeholder="Acme Corp" />
      </div>
      <div className="form-field"><label>Job title</label>
        <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="VP Engineering" />
      </div>
      <div className="form-row">
        <div className="form-field"><label>Email *</label>
          <input type="email" required value={form.email} onChange={e => set("email", e.target.value)} placeholder="jane@company.com" />
        </div>
        <div className="form-field"><label>Phone</label>
          <input value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+1 555 000 0000" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>LinkedIn</label>
          <input value={form.linkedin} onChange={e => set("linkedin", e.target.value)} placeholder="linkedin.com/in/…" />
        </div>
        <div className="form-field"><label>Seniority</label>
          <select value={form.seniority} onChange={e => set("seniority", e.target.value)}>
            <option value="">— None —</option>
            <option>C-Suite</option><option>VP</option><option>Director</option>
            <option>Manager</option><option>Individual Contributor</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Department</label>
          <select value={form.dept} onChange={e => set("dept", e.target.value)}>
            <option value="">— None —</option>
            {D.DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Region</label>
          <select value={form.region} onChange={e => set("region", e.target.value)}>
            <option value="">— None —</option>
            {D.REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Company size</label>
          <select value={form.headcount} onChange={e => set("headcount", e.target.value)}>
            <option value="">— Unknown —</option>
            {D.HEADCOUNT_BANDS.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Industry</label>
          <select value={form.industry} onChange={e => set("industry", e.target.value)}>
            <option value="">— None —</option>
            {D.INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>Source</label>
          <select value={form.source} onChange={e => set("source", e.target.value)}>
            <option value="">— Unknown —</option>
            {D.LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Source detail</label>
          <input value={form.sourceDetail} onChange={e => set("sourceDetail", e.target.value)} placeholder="LinkedIn ad campaign Q2" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-field"><label>GDPR consent</label>
          <select value={form.consent} onChange={e => set("consent", e.target.value)}>
            <option>Unknown</option><option>Opted in</option><option>Opted out</option>
            <option>Legitimate interest</option>
          </select>
        </div>
        <div className="form-field"><label>Owner</label>
          <select value={form.owner} onChange={e => set("owner", e.target.value)}>
            <option value="">— Unassigned —</option>
            {assignableReps(null, form.owner).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>
      <div className="form-field"><label>Group</label>
        <input
          value={form.group}
          onChange={e => set("group", e.target.value)}
          placeholder="none"
          list="lead-group-suggestions"
        />
        <datalist id="lead-group-suggestions">
          {groupSuggestions.map(g => <option key={g} value={g} />)}
        </datalist>
        <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
          A free-text label to group leads together (e.g. a campaign or batch). Defaults to <strong>none</strong>.
        </p>
      </div>
      <div className="form-field">
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 400 }}>
          <input type="checkbox" checked={form.shortlisted} onChange={e => set("shortlisted", e.target.checked)} />
          <span>Add to shortlist</span>
        </label>
        <p className="muted" style={{ margin: "4px 0 0 24px", fontSize: 12 }}>
          Drops this lead straight onto the Shortlist tab — your curated focus list.
        </p>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function ImportLeadsDialog({ onClose }) {
  const [file, setFile] = useState(null);
  const [done, setDone] = useState(false);
  function handleImport() {
    if (!file) return;
    setDone(true);
    setTimeout(onClose, 1400);
  }
  return (
    <Dialog title="Import leads" onClose={onClose} footer={!done && <>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleImport} disabled={!file}>Import</button>
    </>}>
      {done ? <div className="quick-saved">✓ Import queued — leads will appear shortly</div> : <>
        <div className="form-field"><label>CSV file</label>
          <input type="file" accept=".csv" onChange={e => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 12.5 }}>Required columns: first, last, email, company, title. Optional: phone, source, owner.</p>
      </>}
    </Dialog>
  );
}

function DisqualifyLeadDialog({ lead, onClose, onDone }) {
  const reasons = getDqReasons();
  const initial = reasons[0];
  const [reason, setReason] = useState(initial.label);
  const [outcome, setOutcome] = useState(initial.outcome);
  const [recallDate, setRecallDate] = useState(todayPlusMonths(initial.recallMonths));
  const [recallReason, setRecallReason] = useState("");
  // Recall task is opt-in: defaults ON for Nurture (most users want the
  // reminder) and OFF for Disqualified. The user can flip either to handle
  // edge cases (silent nurture with no reminder; disqualified-but-revisit).
  const [createTask, setCreateTask] = useState(initial.outcome === "Nurture");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function onReasonChange(next) {
    const meta = reasons.find(r => r.label === next) || reasons[0];
    setReason(next);
    setOutcome(meta.outcome);
    setRecallDate(todayPlusMonths(meta.recallMonths));
    // Reset task default to match the reason's outcome — the user can
    // still toggle it below before saving.
    setCreateTask(meta.outcome === "Nurture");
  }

  function onOutcomeChange(next) {
    setOutcome(next);
    setCreateTask(next === "Nurture");
  }

  async function handleSave() {
    setSaving(true); setError(null);
    try {
      if (outcome === "Nurture") {
        await tpAPI.leads.update(lead.id, {
          status: "Nurture",
          dq: reason,
          recallDate: recallDate || null,
          recallReason: recallReason.trim() || null,
        });
        if (createTask && recallDate) {
          try {
            await tpAPI.tasks.create({
              id: "t_" + Math.random().toString(36).slice(2, 10),
              type: "follow-up",
              title: `Follow up: ${lead.first} ${lead.last} · ${lead.company || ""}`.trim(),
              dueDate: recallDate,
              status: "Open",
              notes: recallReason.trim() || `Recall after ${reason}`,
              leadId: lead.id,
              owner: lead.owner || null,
              created: new Date().toISOString().slice(0, 10),
            });
          } catch (taskErr) {
            // Lead is already nurtured; surface the task-create failure
            // without rolling back the status change.
            setError(`Lead moved to Nurture, but follow-up task could not be created: ${taskErr.message || taskErr}`);
            setSaving(false);
            return;
          }
        }
      } else {
        await tpAPI.leads.update(lead.id, {
          status: "Disqualified",
          dq: reason,
          recallDate: null,
          recallReason: null,
        });
      }
      onDone();
    } catch (e) {
      setError(e.message || "Failed to update lead.");
      setSaving(false);
    }
  }

  const isNurture = outcome === "Nurture";
  return (
    <Dialog
      title={isNurture ? "Move lead to nurture" : "Disqualify lead"}
      onClose={onClose}
      footer={<>
        <button className="btn btn-sm" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-sm"
          style={isNurture ? {} : { background: "var(--red)", color: "#fff", borderColor: "var(--red)" }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : isNurture ? "Move to nurture" : "Disqualify"}
        </button>
      </>}
    >
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Take <strong>{lead.first} {lead.last}</strong> off the active sales list.{" "}
        {isNurture
          ? "They stay on marketing nurture and resurface on the recall date. Tick the box below to drop a follow-up task on Follow-ups for that date."
          : "Permanently disqualified — off sales and marketing. Kept for reporting only."}
      </p>
      <div className="form-field"><label>Reason</label>
        <select value={reason} onChange={e => onReasonChange(e.target.value)}>
          {reasons.map(r => <option key={r.label}>{r.label}</option>)}
        </select>
      </div>
      <div className="form-field"><label>Outcome</label>
        <select value={outcome} onChange={e => onOutcomeChange(e.target.value)}>
          <option value="Nurture">Nurture (recycle later)</option>
          <option value="Disqualified">Disqualified (terminal)</option>
        </select>
      </div>
      {isNurture && (
        <>
          <div className="form-field"><label>Recall date</label>
            <input type="date" value={recallDate} onChange={e => setRecallDate(e.target.value)} />
          </div>
          <div className="form-field"><label>Recall note</label>
            <textarea
              value={recallReason}
              onChange={e => setRecallReason(e.target.value)}
              placeholder="e.g. mid-massive IT project until winter; revisit after their go-live"
            />
          </div>
          <div className="form-field">
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={createTask}
                onChange={e => setCreateTask(e.target.checked)}
                disabled={!recallDate}
              />
              <span>Create a follow-up task on the recall date</span>
            </label>
            <p className="muted" style={{ margin: "4px 0 0 24px", fontSize: 12 }}>
              {recallDate
                ? "Adds a row to Follow-ups so the recall doesn't expire silently."
                : "Set a recall date above to enable the follow-up task."}
            </p>
          </div>
        </>
      )}
      {error && <div style={{ color: "var(--red)", fontSize: 12.5, marginTop: 8 }}>{error}</div>}
    </Dialog>
  );
}

function ReactivateLeadDialog({ lead, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const openTasks = (D.TASKS || []).filter(t => t.leadId === lead.id && t.status === "Open");
  async function handleSave() {
    setSaving(true); setError(null);
    try {
      await tpAPI.leads.update(lead.id, {
        status: "New",
        recallDate: null,
        recallReason: null,
        dq: null,
      });
      // Close any outstanding follow-up tasks so the lead isn't both
      // active and queued for follow-up at the same time.
      const today = new Date().toISOString().slice(0, 10);
      for (const t of openTasks) {
        try { await tpAPI.tasks.update(t.id, { status: "Done", completedAt: today }); }
        catch (e) { /* lead is already re-activated; task cleanup is best-effort */ }
      }
      onDone();
    } catch (e) {
      setError(e.message || "Failed to reactivate lead.");
      setSaving(false);
    }
  }
  return (
    <Dialog title="Re-activate lead" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
        {saving ? "Saving…" : "Re-activate"}
      </button>
    </>}>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Move <strong>{lead.first} {lead.last}</strong> back to <strong>New</strong> and clear the recall date and DQ reason. They'll need re-qualifying.
        {openTasks.length > 0 && <> {openTasks.length} open follow-up{openTasks.length > 1 ? "s" : ""} will be marked Done.</>}
      </p>
      {error && <div style={{ color: "var(--red)", fontSize: 12.5, marginTop: 8 }}>{error}</div>}
    </Dialog>
  );
}

function ConvertLeadDialog({ lead, onClose, navigate }) {
  const accounts = D.ACCOUNTS || [];
  // Role in deal is per-opportunity now, so it's not captured here — set it
  // when the new contact is attached to a specific deal.
  const [form, setForm] = useState({ accountId: "", gdpr: lead.consent || "Unknown" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function handleSave() {
    if (!form.accountId) { setError("Select an account."); return; }
    setSaving(true); setError(null);
    try {
      const id = "c_" + Math.random().toString(36).slice(2, 9);
      const res = await tpAPI.contacts.create({
        id, first: lead.first, last: lead.last, title: lead.title,
        email: lead.email, phone: lead.phone, seniority: lead.seniority, dept: lead.dept,
        ...form,
      });
      const contactId = res.id || id;
      // Re-point the lead's tasks at the new contact before deleting the
      // lead: tasks.leadId is a non-cascading FK (foreign_keys=ON), so an
      // unmoved task would block the delete — and re-pointing keeps the
      // follow-up history attached to the person rather than orphaning it.
      const leadTasks = (D.TASKS || []).filter(t => t.leadId === lead.id);
      for (const t of leadTasks) {
        try { await tpAPI.tasks.update(t.id, { leadId: null, contactId }); }
        catch (e) { /* best-effort; the delete below will report if it's still blocked */ }
      }
      // The lead is now a contact — remove it from the Leads list.
      await tpAPI.leads.remove(lead.id);
      onClose();
      navigate("sales/contacts/" + contactId);
    } catch(e) { setError(e.message || "Failed to convert lead."); setSaving(false); }
  }
  return (
    <Dialog title="Convert lead to contact" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-accent btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Converting…" : "Convert →"}</button>
    </>}>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Creates a contact for <strong>{lead.first} {lead.last}</strong> and removes them from the Leads list. Any follow-up tasks move to the new contact.</p>
      <div className="form-field"><label>Link to account *</label>
        <select value={form.accountId} onChange={e => set("accountId", e.target.value)}>
          <option value="">— Select account —</option>
          {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </div>
      <p className="muted" style={{ margin: 0, fontSize: 12 }}>
        Attach this contact to an opportunity afterwards to set their role in the deal.
      </p>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

// Quick per-row group assignment. Free-text label (with autocomplete from
// existing groups); blank clears the lead back to "none". Closing the dialog
// is itself a local state change in the parent, so the list re-renders with
// the fresh value once tpReload has refreshed window.TPCRM_DATA.
function SetLeadGroupDialog({ lead, onClose, onDone }) {
  const [group, setGroup] = useState(lead.group && lead.group !== "none" ? lead.group : "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const suggestions = [...new Set((D.LEADS || []).map(l => l.group).filter(g => g && g !== "none"))].sort();
  async function handleSave() {
    setSaving(true); setError(null);
    try {
      await tpAPI.leads.update(lead.id, { group: group.trim() || "none" });
      onDone();
    } catch (e) {
      setError(e.message || "Failed to set group.");
      setSaving(false);
    }
  }
  return (
    <Dialog title="Set group" onClose={onClose} footer={<>
      <button className="btn btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </button>
    </>}>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Group <strong>{lead.first} {lead.last}</strong> with other leads. Leave blank for <strong>none</strong>.
      </p>
      <div className="form-field"><label>Group</label>
        <input
          value={group}
          onChange={e => setGroup(e.target.value)}
          placeholder="none"
          list="lead-group-suggestions-row"
          autoFocus
        />
        <datalist id="lead-group-suggestions-row">
          {suggestions.map(g => <option key={g} value={g} />)}
        </datalist>
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function LeadsList({ navigate }) {
  const [tab, setTab] = useState("all");
  const [status, setStatus] = useState("all");
  const [group, setGroup] = useState("all");
  const [owner, setOwner] = useState("all");
  const [source, setSource] = useState("all");
  const [search, setSearch] = useState("");
  // Shortlisted leads live on their own tab, so by default they're hidden
  // from the All leads list to keep it focused on the un-triaged ones.
  const [hideShortlisted, setHideShortlisted] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [converting, setConverting] = useState(null);
  const [grouping, setGrouping] = useState(null);
  const [error, setError] = useState(null);
  // The global tpReload tick can't reach this subtree (DataProvider returns
  // a referentially-stable <Root/>, so React bails out of re-rendering it —
  // same reason admin.jsx keeps a local tick). For in-place mutations that
  // don't open/close a dialog, bump this after the reload to refresh the list.
  const [, forceRerender] = useState(0);

  const shortlistCount = D.LEADS.filter(l => l.shortlisted).length;
  const tabs = [
    { key: "all",       label: "All leads" },
    { key: "shortlist", label: "Shortlist", count: shortlistCount || undefined },
  ];

  // Filter dropdowns are populated from the distinct values present in the
  // data (same approach as the Group filter). Missing values collapse to a
  // sentinel: "" = unassigned owner / no source.
  const groupOptions = [...new Set(D.LEADS.map(l => l.group || "none"))].sort();
  const ownerOptions = [...new Set(D.LEADS.map(l => l.owner || ""))]
    .sort((a, b) => (getUser(a)?.name || "Unassigned").localeCompare(getUser(b)?.name || "Unassigned"));
  const sourceOptions = [...new Set(D.LEADS.map(l => l.source || ""))].sort();
  const rows = D.LEADS.filter(l => {
    if (tab === "shortlist" && !l.shortlisted) return false;
    if (tab === "all" && hideShortlisted && l.shortlisted) return false;
    if (status !== "all" && l.status !== status) return false;
    if (group !== "all" && (l.group || "none") !== group) return false;
    if (owner !== "all" && (l.owner || "") !== owner) return false;
    if (source !== "all" && (l.source || "") !== source) return false;
    if (search && !`${l.first} ${l.last} ${l.company}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const tabTotal = tab === "shortlist" ? shortlistCount : D.LEADS.length;

  // Toggle shortlist membership from a row button. Stop the click from
  // bubbling to the <tr> (which navigates to the detail page). tpReload
  // refreshes window.TPCRM_DATA; forceRerender then re-renders this list so
  // the star / Shortlist tab reflect the change without a navigation.
  async function toggleShortlist(e, l) {
    e.stopPropagation();
    setError(null);
    try {
      await tpAPI.leads.update(l.id, { shortlisted: !l.shortlisted });
      forceRerender(n => n + 1);
    } catch (err) {
      setError(err.message || "Failed to update shortlist.");
    }
  }

  return (
    <>
      <PageHead title="Leads" sub={`${rows.length} leads`} actions={<>
        <button className="btn btn-sm" onClick={() => setShowImport(true)}>Import</button>
        <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>+ New lead</button>
      </>} />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      <div className="card">
        <FilterToolbar count={rows.length} total={tabTotal}>
          <input type="text" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option value="all">All statuses</option><option>New</option><option>Contacted</option><option>Working</option><option>Qualified</option><option>Nurture</option><option>Disqualified</option>
          </select>
          <select value={group} onChange={e => setGroup(e.target.value)}>
            <option value="all">All groups</option>
            {groupOptions.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={owner} onChange={e => setOwner(e.target.value)}>
            <option value="all">All owners</option>
            {ownerOptions.map(o => <option key={o || "_unassigned"} value={o}>{getUser(o)?.name || "Unassigned"}</option>)}
          </select>
          <select value={source} onChange={e => setSource(e.target.value)}>
            <option value="all">All sources</option>
            {sourceOptions.map(s => <option key={s || "_none"} value={s}>{s || "— None —"}</option>)}
          </select>
          {tab === "all" && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={hideShortlisted} onChange={e => setHideShortlisted(e.target.checked)} />
              Hide shortlisted
            </label>
          )}
        </FilterToolbar>
        {error && <div className="form-error" style={{ margin: "8px 12px" }}>{error}</div>}
        <table className="tbl tbl-flush">
          <thead><tr><th>Name</th><th>Company</th><th>Title</th><th className="right">Score</th><th>Status</th><th>Group</th><th>MQL</th><th></th></tr></thead>
          <tbody>
            {rows.map(l => (
              <tr key={l.id} onClick={() => navigate("sales/leads/" + l.id)}>
                <td><span className="tbl-link">{l.first} {l.last}</span></td>
                <td>{l.company} <span className="muted" style={{ fontSize: 11 }}>· {l.headcount}</span></td>
                <td className="muted">{l.title}</td>
                <td className="right"><Donut value={l.score} size={28} /></td>
                <td><Badge tone={l.status === "Qualified" ? "green" : l.status === "Disqualified" ? "red" : l.status === "Nurture" ? "blue" : l.status === "Working" ? "amber" : "slate"}>{l.status}</Badge>{l.status === "Nurture" && l.recallDate ? <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>recall {fmtDateShort(l.recallDate)}</span> : null}</td>
                <td onClick={e => { e.stopPropagation(); setGrouping(l); }} style={{ cursor: "pointer" }} title="Set group">
                  {l.group && l.group !== "none" ? <Badge tone="blue">{l.group}</Badge> : <span className="muted">+ group</span>}
                </td>
                <td>{l.mqlDate ? fmtDateShort(l.mqlDate) : <span className="muted">—</span>}</td>
                <td className="right">
                  <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
                    <button
                      className="btn btn-sm"
                      title={l.shortlisted ? "Remove from shortlist" : "Add to shortlist"}
                      onClick={e => toggleShortlist(e, l)}
                    >
                      {l.shortlisted ? "★ Shortlisted" : "☆ Shortlist"}
                    </button>
                    {l.status !== "Disqualified" && (
                      <button
                        className="btn btn-accent btn-sm"
                        title="Convert this lead to a contact"
                        onClick={e => { e.stopPropagation(); setConverting(l); }}
                      >
                        → Contact
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="muted" style={{ padding: 20, textAlign: "center" }}>
                {tab === "shortlist"
                  ? "No leads on the shortlist yet. Use the ☆ Shortlist button on the All leads tab to add some."
                  : "No leads match the current filters."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showImport && <ImportLeadsDialog onClose={() => setShowImport(false)} />}
      {showNew && <NewLeadDialog onClose={() => setShowNew(false)} navigate={navigate} />}
      {converting && <ConvertLeadDialog lead={converting} onClose={() => setConverting(null)} navigate={navigate} />}
      {grouping && <SetLeadGroupDialog lead={grouping} onClose={() => setGrouping(null)} onDone={() => setGrouping(null)} />}
    </>
  );
}

function LeadDetail({ id, navigate }) {
  const l = D.LEADS.find(x => x.id === id);
  const [tab, setTab] = useState("overview");
  const [action, setAction] = useState(null);
  const [grouping, setGrouping] = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  // Same re-render workaround as LeadsList: tpReload updates the global data
  // but the tick can't reach this subtree, so bump locally after a save.
  const [, forceRerender] = useState(0);
  if (!l) return <PageHead title="Not found" />;
  const today = new Date().toISOString().slice(0, 10);
  // Inline-edit save: persist a partial patch, then re-render with fresh data.
  async function save(patch) {
    setSaveErr(null);
    try {
      await tpAPI.leads.update(l.id, patch);
      forceRerender(n => n + 1);
    } catch (e) {
      setSaveErr(e.message || "Failed to save.");
    }
  }
  const leadTasks = (D.TASKS || []).filter(t => t.leadId === l.id);
  const openTaskCount = leadTasks.filter(t => t.status === "Open").length;
  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "tasks", label: "Tasks", count: openTaskCount || undefined },
    { key: "activity", label: "Activity" },
    { key: "sequences", label: "Sequences" },
    { key: "qual", label: "Qualification" },
  ];
  return (
    <>
      <PageHead title={`${l.first} ${l.last}`} sub={<>{l.title} · {l.company} · <Badge tone="slate">{l.source}</Badge>{l.status === "Nurture" ? <> · <Badge tone="blue">Nurture</Badge>{l.recallDate ? <span className="muted"> · recall {fmtDateShort(l.recallDate)}</span> : null}</> : l.status === "Disqualified" ? <> · <Badge tone="red">Disqualified</Badge></> : null}</>}
        actions={<>
          {(l.status === "Nurture" || l.status === "Disqualified") && (
            <button className="btn btn-sm" onClick={() => setAction("reactivate")}>Re-activate</button>
          )}
          {l.status !== "Disqualified" && (
            <button className="btn btn-sm" onClick={() => setAction("dq")}>Disqualify / Nurture</button>
          )}
          {l.status !== "Disqualified" && (
            <button className="btn btn-accent btn-sm" onClick={() => setAction("convert")}>Convert →</button>
          )}
        </>} />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      <div className="detail">
        <div>
          {tab === "overview" && (
            <div className="card"><div className="card-head"><div className="card-title">Lead overview</div></div>
            <div className="card-body">
              {saveErr && <div className="form-error" style={{ marginBottom: 12 }}>{saveErr}</div>}
              <div className="grid grid-2" style={{ gap: 24 }}>
              <div className="kv">
                <div className="kv-label">Email</div>
                <div className="kv-value"><KvText value={l.email} type="email" allowEmpty={false} placeholder="jane@company.com" onSave={v => save({ email: v })} /></div>
                <div className="kv-label">Phone</div>
                <div className="kv-value"><KvText value={l.phone} placeholder="+1 555 000 0000" onSave={v => save({ phone: v })} /></div>
                <div className="kv-label">LinkedIn</div>
                <div className="kv-value" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <KvText value={l.linkedin} placeholder="jane-doe" onSave={v => save({ linkedin: v })} />
                  {linkedinHref(l.linkedin) && (
                    <a href={linkedinHref(l.linkedin)} target="_blank" rel="noopener noreferrer"
                       title={linkedinHref(l.linkedin)}
                       style={{ flexShrink: 0, fontSize: 15, textDecoration: "none" }}>↗</a>
                  )}
                </div>
                <div className="kv-label">Seniority</div>
                <div className="kv-value"><KvSelect value={l.seniority} options={SENIORITIES} onChange={v => save({ seniority: v })} /></div>
                <div className="kv-label">Department</div>
                <div className="kv-value"><KvSelect value={l.dept} options={D.DEPARTMENTS} onChange={v => save({ dept: v })} /></div>
                <div className="kv-label">Region</div>
                <div className="kv-value"><KvSelect value={l.region} options={D.REGIONS} onChange={v => save({ region: v })} /></div>
                <div className="kv-label">Company size</div>
                <div className="kv-value"><KvSelect value={l.headcount} options={D.HEADCOUNT_BANDS} blank="— Unknown —" onChange={v => save({ headcount: v })} /></div>
                <div className="kv-label">Industry</div>
                <div className="kv-value"><KvSelect value={l.industry} options={D.INDUSTRIES} onChange={v => save({ industry: v })} /></div>
              </div>
              <div className="kv">
                <div className="kv-label">Lead source</div>
                <div className="kv-value"><KvSelect value={l.source} options={D.LEAD_SOURCES} blank="— Unknown —" onChange={v => save({ source: v })} /></div>
                <div className="kv-label">Source detail</div>
                <div className="kv-value"><KvText value={l.sourceDetail} placeholder="LinkedIn ad campaign Q2" onSave={v => save({ sourceDetail: v })} /></div>
                <div className="kv-label">Created</div><div className="kv-value">{fmtDate(l.created)}</div>
                <div className="kv-label">Owner</div>
                <div className="kv-value"><KvSelect value={l.owner} options={assignableReps(null, l.owner).map(u => ({ value: u.id, label: u.name }))} blank="— Unassigned —" onChange={v => save({ owner: v })} /></div>
                <div className="kv-label">Group</div>
                <div className="kv-value" onClick={() => setGrouping(l)} style={{ cursor: "pointer" }} title="Set group">
                  {l.group && l.group !== "none" ? <Badge tone="blue">{l.group}</Badge> : <span className="muted">+ group</span>}
                </div>
                <div className="kv-label">Lead score</div>
                <div className="kv-value" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Donut value={l.score} size={28} />
                  <KvText value={l.score} type="number" onSave={v => save({ score: v == null ? null : Number(v) })} />
                </div>
                <div className="kv-label">MQL</div>
                <div className="kv-value"><DateToggle date={l.mqlDate} onToggle={on => save({ mqlDate: on ? today : null })} /></div>
                <div className="kv-label">SQL</div>
                <div className="kv-value"><DateToggle date={l.sqlDate} onToggle={on => save({ sqlDate: on ? today : null })} /></div>
                <div className="kv-label">Intent signal</div>
                <div className="kv-value">
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!l.intent} onChange={e => save({ intent: e.target.checked, intentDate: e.target.checked ? today : null })} />
                    {l.intent
                      ? <><Badge tone="amber">{l.intentSource || "Intent"}</Badge>{l.intentDate ? <span className="muted" style={{ marginLeft: 6 }}>{fmtMonYY(l.intentDate)}</span> : null}</>
                      : <span className="muted">Off</span>}
                  </label>
                </div>
                <div className="kv-label">GDPR</div>
                <div className="kv-value"><KvSelect value={l.consent} options={CONSENT_OPTIONS} blank={null} onChange={v => save({ consent: v || "Unknown" })} /></div>
                <div className="kv-label">DNC</div>
                <div className="kv-value">
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={!!l.dnc} onChange={e => save({ dnc: e.target.checked })} />
                    {l.dnc ? <Badge tone="red">Do not contact</Badge> : <span className="muted">Contactable</span>}
                  </label>
                </div>
                {l.status === "Nurture" && <>
                  <div className="kv-label">Recall date</div><div className="kv-value">{l.recallDate ? fmtDate(l.recallDate) : <span className="muted">—</span>}</div>
                  <div className="kv-label">Recall note</div><div className="kv-value muted">{l.recallReason || "—"}</div>
                </>}
                {l.dq && <>
                  <div className="kv-label">DQ reason</div><div className="kv-value">{l.dq}</div>
                </>}
              </div>
            </div></div></div>
          )}
          {tab === "tasks" && (
            leadTasks.length === 0
              ? <div className="card"><div className="card-body muted">No tasks for this lead. Move them to <strong>Nurture</strong> with a recall date and a follow-up task will be created automatically.</div></div>
              : <div className="card">
                  <table className="tbl tbl-flush">
                    <thead><tr><th>Title</th><th>Due</th><th>Owner</th><th>Status</th><th>Notes</th><th></th></tr></thead>
                    <tbody>
                      {leadTasks.map(t => (
                        <tr key={t.id} style={{ cursor: "default" }}>
                          <td>{t.title}</td>
                          <td>{t.dueDate ? fmtDateShort(t.dueDate) : <span className="muted">—</span>}</td>
                          <td>{t.owner ? <Avatar user={t.owner} size={20} /> : <span className="muted">—</span>}</td>
                          <td><Badge tone={t.status === "Done" ? "green" : t.status === "Cancelled" ? "slate" : "amber"}>{t.status}</Badge></td>
                          <td className="muted" style={{ maxWidth: 280 }}>{t.notes || "—"}</td>
                          <td className="right">
                            {t.status === "Open" && (
                              <button className="btn btn-sm" onClick={async () => {
                                await tpAPI.tasks.update(t.id, { status: "Done", completedAt: new Date().toISOString().slice(0, 10) });
                              }}>Mark done</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
          )}
          {tab === "activity" && <div className="card"><div className="card-body muted">No activity recorded for this lead.</div></div>}
          {tab === "sequences" && <div className="card"><div className="card-body muted">This lead is not enrolled in any sequences.</div></div>}
          {tab === "qual" && (
            <div className="card"><div className="card-body">
              <div className="medd-row confirmed"><div className="medd-letter">★</div><div><div className="medd-label">ICP fit</div><div className="medd-desc">Firmographic + technographic match</div></div><Badge tone="green">Yes</Badge></div>
              <div className="medd-row confirmed"><div className="medd-letter">★</div><div><div className="medd-label">Role match</div><div className="medd-desc">Seniority and department fit ICP buyer profile</div></div><Badge tone="green">Yes</Badge></div>
              <div className="medd-row"><div className="medd-letter">★</div><div><div className="medd-label">Pain confirmed</div><div className="medd-desc">Specific pain articulated in conversation</div></div><Badge tone="amber">Pending</Badge></div>
              {l.dq && <div className="medd-row"><div className="medd-letter">⊘</div><div><div className="medd-label">Off-pipeline reason</div><div className="medd-desc">{l.dq}{l.status === "Nurture" && l.recallDate ? ` · recall ${fmtDateShort(l.recallDate)}` : ""}</div></div><Badge tone={l.status === "Nurture" ? "blue" : "red"}>{l.status === "Nurture" ? "Nurture" : "DQ"}</Badge></div>}
            </div></div>
          )}
        </div>
        <RightRail record={{...l, createdBy: l.owner}} />
      </div>
      {action === "dq" && <DisqualifyLeadDialog lead={l} onClose={() => setAction(null)} onDone={() => setAction(null)} />}
      {action === "reactivate" && <ReactivateLeadDialog lead={l} onClose={() => setAction(null)} onDone={() => setAction(null)} />}
      {action === "convert" && <ConvertLeadDialog lead={l} onClose={() => setAction(null)} navigate={navigate} />}
      {grouping && <SetLeadGroupDialog lead={l} onClose={() => setGrouping(null)} onDone={() => setGrouping(null)} />}
    </>
  );
}

window.LeadsList = LeadsList;
window.LeadDetail = LeadDetail;
